// Dashboard page component
import { createClerkSupabaseClient } from '@/lib/supabase'
import { useProject } from '@/contexts/project-context'
import { useQuery } from '@tanstack/react-query'
import { useUser, usePermissions, useAuth } from '@/lib/hooks'
import { useGetToken } from '@/lib/hooks/use-auth'
import { Link } from 'react-router-dom'
import { 
  Users, 
  DollarSign, 
  TrendingUp, 
  Activity,
  Gift,
  ArrowRight
} from 'lucide-react'
import { GettingStartedChecklist } from '@/components/dashboard/getting-started-checklist'

export default function DashboardPage() {
  const { currentProject } = useProject()
  const { profile } = useUser()
  const { user } = useAuth()
  const getToken = useGetToken()
  const permissions = usePermissions()
  const userEmail = user?.email || ''

  // Fetch pending equity offers
  const { data: pendingOffers } = useQuery({
    queryKey: ['pending-equity-offers', profile?.id, currentProject?.id],
    queryFn: async () => {
      if (!profile?.id) return []
      
      // Create authenticated Supabase client with Clerk token
      const authenticatedClient = createClerkSupabaseClient(getToken)
      
      const query = authenticatedClient
        .from('equity_offers')
        .select('id, proposed_equity_percentage, project_id')
        .eq('user_id', profile.id)
        .eq('status', 'pending')

      if (currentProject) {
        query.eq('project_id', currentProject.id)
      }

      const { data, error } = await query
      if (error) throw error
      return data || []
    },
    enabled: !!profile?.id
  })

  // Simplified stats for debugging
  const stats = {
    totalUsers: 0,
    totalContributions: 0,
    totalEquityAllocated: 0,
    activeSimulations: 0
  }

  const statCards = [
    {
      title: 'Total Users',
      value: stats.totalUsers,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      title: 'Contributions',
      value: stats.totalContributions,
      icon: Activity,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      title: 'Equity Allocated',
      value: `${stats.totalEquityAllocated.toFixed(2)}%`,
      icon: DollarSign,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    },
    {
      title: 'Simulations',
      value: stats.activeSimulations,
      icon: TrendingUp,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100'
    }
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">
          {currentProject ? currentProject.name : 'Dashboard'}
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          Welcome back, {userEmail}
        </p>
      </div>

      {/* Pending Equity Offers Notification */}
      {pendingOffers && pendingOffers.length > 0 && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Gift className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-purple-900">
                  You have {pendingOffers.length} pending equity offer{pendingOffers.length > 1 ? 's' : ''}
                </h3>
                <div className="mt-1 text-sm text-purple-700">
                  {pendingOffers.map(offer => `${offer.proposed_equity_percentage}%`).join(', ')} equity waiting for your review
                </div>
              </div>
            </div>
            <Link
              to="/equity-offers"
              className="ml-4 flex items-center text-sm font-medium text-purple-600 hover:text-purple-500"
            >
              Review offers
              <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </div>
        </div>
      )}

      {/* Getting Started Checklist */}
      <div className="mb-6">
        <GettingStartedChecklist />
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat, index) => (
          <div key={index} className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className={`flex-shrink-0 ${stat.bgColor} rounded-md p-3`}>
                  <stat.icon className={`h-6 w-6 ${stat.color}`} />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {stat.title}
                    </dt>
                    <dd className="text-2xl font-semibold text-gray-900">
                      {stat.value}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Different view for investors/advisors */}
      {permissions.isViewOnly ? (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Portfolio Overview</h2>
            <div className="space-y-3">
              <Link 
                to="/cap-table" 
                className="block w-full text-left px-4 py-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors"
              >
                <span className="text-sm font-medium text-gray-900">View Cap Table</span>
                <p className="text-xs text-gray-500 mt-1">Review current equity distribution</p>
              </Link>
              <Link 
                to="/funding-rounds" 
                className="block w-full text-left px-4 py-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors"
              >
                <span className="text-sm font-medium text-gray-900">Funding History</span>
                <p className="text-xs text-gray-500 mt-1">View funding rounds and valuations</p>
              </Link>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Company Activity</h2>
            <div className="space-y-3">
              <Link 
                to="/contributions" 
                className="block w-full text-left px-4 py-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors"
              >
                <span className="text-sm font-medium text-gray-900">View Contributions</span>
                <p className="text-xs text-gray-500 mt-1">Track team activity and progress</p>
              </Link>
              <Link 
                to="/tasks" 
                className="block w-full text-left px-4 py-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors"
              >
                <span className="text-sm font-medium text-gray-900">Task Overview</span>
                <p className="text-xs text-gray-500 mt-1">See what the team is working on</p>
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h2>
            <div className="space-y-3">
              <p className="text-sm text-gray-500">No recent activity to display.</p>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h2>
            <div className="space-y-3">
              <Link 
                to="/contributions" 
                className="block w-full text-left px-4 py-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors"
              >
                <span className="text-sm font-medium text-gray-900">Log a Contribution</span>
                <p className="text-xs text-gray-500 mt-1">Record your work and activities</p>
              </Link>
              <Link 
                to="/simulations" 
                className="block w-full text-left px-4 py-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors"
              >
                <span className="text-sm font-medium text-gray-900">Run Simulation</span>
                <p className="text-xs text-gray-500 mt-1">Test equity scenarios</p>
              </Link>
              <Link 
                to="/cap-table" 
                className="block w-full text-left px-4 py-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors"
              >
                <span className="text-sm font-medium text-gray-900">View Cap Table</span>
                <p className="text-xs text-gray-500 mt-1">Check current equity distribution</p>
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}